/* Enhances native variant selects; product-info remains responsible for fetching variants. */
(() => {
  if (customElements.get('rl-size-picker')) return;

  class RLSizePicker extends HTMLElement {
    static pendingFocus = new Map();

    connectedCallback() {
      // Parser-created custom elements may connect before their children exist.
      queueMicrotask(() => {
        if (this.isConnected && !this.controller) this.initialize();
      });
    }

    initialize() {
      this.select = this.querySelector('[data-rl-size-native]');
      this.trigger = this.querySelector('[data-rl-size-trigger]');
      this.list = this.querySelector('[data-rl-size-list]');
      this.selectedLabel = this.querySelector('[data-rl-size-selected]');
      this.guide = this.querySelector('[data-rl-size-guide]');
      this.guideLink = this.querySelector('[data-rl-size-guide-open]');
      if (!this.select || !this.trigger || !this.list || !this.selectedLabel) return;

      let values;
      try {
        values = JSON.parse(this.querySelector('[data-rl-size-values]').textContent);
        if (!Array.isArray(values)) return;
      } catch {
        return; // Preserve the native select if enhancement data is invalid.
      }
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.focusKey = this.select.id;
      const byId = new Map(values.map((value) => [String(value.id), value]));
      this.list.replaceChildren();
      this.items = Array.from(this.select.options).map((option, index) => {
        const metadata = byId.get(option.dataset.optionValueId) || values[index];
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rl-size-picker__option';
        button.id = `${this.list.id}-option-${index}`;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(option.selected));
        button.tabIndex = -1;
        button.dataset.index = String(index);
        button.dataset.label = metadata?.value ?? option.value;
        button.dataset.available = String(metadata?.available !== false);
        const label = document.createElement('span');
        label.textContent = metadata?.value ?? option.value;
        button.append(label);
        if (metadata?.available === false) {
          const status = document.createElement('span');
          status.className = 'rl-size-picker__sold-out';
          status.textContent = this.dataset.soldOut || window.variantStrings?.soldOut || '';
          button.classList.add('rl-size-picker__option--sold-out');
          button.append(status);
        }
        button.addEventListener('click', () => this.choose(index), { signal });
        this.list.append(button);
        return button;
      });
      this.syncSelection();
      this.trigger.hidden = false;
      this.classList.add('rl-size-picker--enhanced');
      this.pointerWasOpen = null;
      this.trigger.addEventListener('pointerdown', (event) => {
        if (event.isPrimary && event.button === 0) this.pointerWasOpen = !this.list.hidden;
      }, { signal });
      this.trigger.addEventListener('pointercancel', () => {
        this.pointerWasOpen = null;
      }, { signal });
      this.trigger.addEventListener('click', () => {
        const wasOpen = this.pointerWasOpen ?? !this.list.hidden;
        this.pointerWasOpen = null;
        if (wasOpen) this.close();
        else this.open(this.select.selectedIndex);
      }, { signal });
      this.trigger.addEventListener('keydown', (event) => {
        this.pointerWasOpen = null;
        this.onTriggerKeydown(event);
      }, { signal });
      this.querySelector('.rl-size-picker__label')?.addEventListener('click', (event) => {
        event.preventDefault();
        this.trigger.focus();
      }, { signal });
      this.list.addEventListener('keydown', (event) => this.onListKeydown(event), { signal });
      this.select.addEventListener('change', () => this.syncSelection(), { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.trigger.contains(event.target)) this.pointerWasOpen = null;
        if (!this.contains(event.target)) this.close(false);
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget) {
          if (!this.contains(event.relatedTarget)) this.close(false);
          return;
        }
        // A microtask can run before the browser finishes moving pointer focus.
        // Wait until that transition finishes, so clicking an option stays open
        // long enough for its click event to select the corresponding value.
        setTimeout(() => {
          if (this.isConnected && !this.contains(document.activeElement)) this.close(false);
        }, 0);
      }, { signal });

      if (this.guide && typeof HTMLDialogElement !== 'undefined' &&
          typeof HTMLDialogElement.prototype.showModal === 'function') {
        // Keep one copy of the guide. Without JS the local anchor reveals it;
        // with JS, move that same content into an accessible native dialog.
        this.dialog = this.guide.closest('dialog');
        if (!this.dialog) {
          this.dialog = document.createElement('dialog');
          this.dialog.id = `${this.guide.id}-dialog`;
          this.dialog.className = 'rl-size-picker__dialog';
          this.dialog.setAttribute('aria-labelledby', this.guide.getAttribute('aria-labelledby'));
          this.dialog.setAttribute('aria-describedby', this.guide.getAttribute('aria-describedby'));
          this.guide.before(this.dialog);
          this.dialog.append(this.guide);
        }
        this.guideLink.setAttribute('aria-haspopup', 'dialog');
        this.guideLink.setAttribute('aria-controls', this.dialog.id);
        this.guideLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.close(false);
          if (!this.dialog.open) {
            this.dialog.showModal();
            this.querySelector('[data-rl-size-guide-close]').focus({ preventScroll: true });
          }
        }, { signal });
        this.querySelector('[data-rl-size-guide-close]').addEventListener('click', (event) => {
          event.preventDefault();
          this.dialog.close();
        }, { signal });
        this.dialog.addEventListener('click', (event) => {
          // Close only on the backdrop, not whitespace inside the dialog.
          const bounds = this.dialog.getBoundingClientRect();
          if (event.target === this.dialog &&
              (event.clientX < bounds.left || event.clientX > bounds.right ||
               event.clientY < bounds.top || event.clientY > bounds.bottom)) this.dialog.close();
        }, { signal });
        this.dialog.addEventListener('close', () => {
          if (this.isConnected) this.guideLink.focus({ preventScroll: true });
        }, { signal });
      }

      // The theme replaces variant-selects after its fetch. Restore focus to the
      // visible control only when the shopper has not moved to another control.
      const pending = RLSizePicker.pendingFocus.get(this.focusKey);
      if (pending) {
        RLSizePicker.pendingFocus.delete(this.focusKey);
        const active = document.activeElement;
        if (Date.now() - pending.time < 10000 &&
            (active === document.body || active === pending.trigger || !active?.isConnected)) {
          requestAnimationFrame(() => {
            if (this.isConnected) this.trigger.focus({ preventScroll: true });
          });
        }
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.centerFrame);
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
    }

    syncSelection() {
      const index = this.select.selectedIndex;
      this.items.forEach((item, itemIndex) => {
        item.setAttribute('aria-selected', String(itemIndex === index));
      });
      const item = this.items[index];
      this.selectedLabel.textContent = item
        ? `${item.dataset.label}${item.dataset.available === 'false' ? ` · ${this.dataset.soldOut || window.variantStrings?.soldOut || ''}` : ''}`
        : this.dataset.chooseSize || '';
    }

    open(index = this.select.selectedIndex, { center = false } = {}) {
      if (!this.items.length) return;
      this.list.style.removeProperty('max-height');
      this.list.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.focusOption(index < 0 ? 0 : index, !center);
      if (center) this.centerOpenPicker();
    }

    centerOpenPicker() {
      cancelAnimationFrame(this.centerFrame);
      this.centerFrame = requestAnimationFrame(() => {
        if (!this.isConnected || this.list.hidden) return;
        const viewport = window.visualViewport;
        const viewportTop = viewport?.offsetTop || 0;
        const viewportHeight = viewport?.height || window.innerHeight;
        const header = document.querySelector('.section-header');
        const headerHeight = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position)
          ? header.getBoundingClientRect().height : 0;
        const sticky = this.closest('product-info')?.querySelector('.sh-sticky-atc');
        const stickyHeight = sticky && getComputedStyle(sticky).display !== 'none'
          ? sticky.getBoundingClientRect().height : 0;
        const safeTop = viewportTop + headerHeight + 16;
        const safeBottom = viewportTop + viewportHeight - stickyHeight - 16;
        const control = this.getBoundingClientRect();
        const menu = this.list.getBoundingClientRect();

        // Fit the whole control and menu between the fixed header and buy bar.
        // On short screens only the options scroll, rather than the page.
        const menuSpace = Math.max(48, safeBottom - safeTop - (menu.top - control.top));
        this.list.style.maxHeight = `${Math.min(menu.height, menuSpace)}px`;
        const item = this.items[this.select.selectedIndex] || this.items[0];
        this.list.scrollTop = Math.max(0, item.offsetTop - (this.list.clientHeight - item.offsetHeight) / 2);
        const height = this.list.getBoundingClientRect().bottom - control.top;
        const centeredTop = viewportTop + (viewportHeight - height) / 2;
        const top = Math.max(safeTop, Math.min(centeredTop, safeBottom - height));
        window.scrollTo({
          top: Math.max(0, window.scrollY + control.top - top),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
      });
    }

    close(returnFocus = true) {
      cancelAnimationFrame(this.centerFrame);
      if (!this.list || this.list.hidden) return;
      this.list.hidden = true;
      this.list.style.removeProperty('max-height');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.trigger.focus({ preventScroll: true });
    }

    focusOption(index, scroll = true) {
      const normalized = (index + this.items.length) % this.items.length;
      this.items.forEach((item, itemIndex) => { item.tabIndex = itemIndex === normalized ? 0 : -1; });
      this.items[normalized].focus({ preventScroll: true });
      if (scroll) this.items[normalized].scrollIntoView({ block: 'nearest' });
    }

    choose(index) {
      const option = this.select.options[index];
      if (!option || option.disabled) return;
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.syncSelection();
      this.close();
      if (changed) {
        RLSizePicker.pendingFocus.set(this.focusKey, { trigger: this.trigger, time: Date.now() });
        // Keep the native selected attribute untouched until VariantSelects updates
        // it itself, then lets product-info fetch with data-option-value-id.
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // An explicit choice also counts when it matches the initial variant.
      this.dispatchEvent(new CustomEvent('rl:size-selected', { bubbles: true }));
    }

    onTriggerKeydown(event) {
      const index = this.select.selectedIndex;
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          this.open(index);
          break;
        case 'Home':
        case 'End':
          event.preventDefault();
          this.open(event.key === 'Home' ? 0 : this.items.length - 1);
          break;
        case 'Escape':
          this.close();
          break;
      }
    }

    onListKeydown(event) {
      const item = event.target.closest('[role="option"]');
      if (!item) return;
      const index = Number(item.dataset.index);
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          this.focusOption(index + (event.key === 'ArrowDown' ? 1 : -1));
          break;
        case 'Home':
        case 'End':
          event.preventDefault();
          this.focusOption(event.key === 'Home' ? 0 : this.items.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.choose(index);
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          this.close();
          break;
      }
    }
  }

  customElements.define('rl-size-picker', RLSizePicker);
})();
